import { useState } from 'react'

import { ToastAction } from '@/components/ui/Toast'
import { useToast } from '@/hooks'
import { useProjectMutations } from '@/modules/projects'
import type {
  Edit,
  EditPreview,
  EditRequestDTO,
} from '@/modules/projects/stores/types'

// Long enough to read the summary and change your mind; the history keeps the undo after that.
const UNDO_TOAST_MS = 12_000

export type SendEdit = (request: EditRequestDTO) => Promise<void>

export interface PendingEdits {
  requests: EditRequestDTO[]
  previews: EditPreview[] | null
  problem: string | null
  // Set for an edit the log only previews, such as a re-cut: the page sends it its own way.
  send: SendEdit | null
}

const summaryOf = (edits: Pick<Edit, 'summary'>[]) =>
  edits.map((edit) => edit.summary).join('. ')

const messageOf = (error: unknown) =>
  error instanceof Error ? error.message : String(error)

// One at a time, stopping at the first failure, with whatever finished before it.
const runInOrder = async <Done,>(
  requests: EditRequestDTO[],
  step: (request: EditRequestDTO) => Promise<Done>,
) => {
  const done: Done[] = []
  try {
    for (const request of requests) done.push(await step(request))
    return { done, error: null }
  } catch (error) {
    return { done, error }
  }
}

// Every hand edit on the page goes the same way: lukita previews it against the real rows, the user
// reads what moves and what it costs, and only a confirm writes it. A saved edit offers its undo.
export const useConfirmedEdits = () => {
  const { previewEdit, commitEdit, undoEdit } = useProjectMutations()
  const { toast, errorToast } = useToast()
  const [pending, setPending] = useState<PendingEdits | null>(null)
  const [saving, setSaving] = useState(false)

  const stillAsked = (
    requests: EditRequestDTO[],
    next: Partial<PendingEdits>,
  ) =>
    setPending((current) =>
      current?.requests === requests ? { ...current, ...next } : current,
    )

  // Undone newest first: a later edit of the same account has to come off before an earlier one.
  const undo = async (edits: Edit[]) => {
    try {
      for (const edit of [...edits].reverse()) await undoEdit(edit.id)
      toast({ title: 'Change undone', description: summaryOf(edits) })
    } catch (error) {
      errorToast('Could not undo that change', messageOf(error))
    }
  }

  const announce = (edits: Edit[]) =>
    toast({
      title: 'Change saved',
      description: summaryOf(edits),
      duration: UNDO_TOAST_MS,
      action: (
        <ToastAction
          altText="Undo this change"
          className="cursor-pointer"
          onClick={() => undo(edits)}
        >
          Undo
        </ToastAction>
      ),
    })

  // Whatever saved before a failure is kept and offered for undo.
  const saveEach = async (requests: EditRequestDTO[]) => {
    const saved = await runInOrder(requests, commitEdit)
    if (saved.done.length > 0) announce(saved.done)
    return saved
  }

  const commit = async (requests: EditRequestDTO[]) => {
    const { error } = await saveEach(requests)
    if (error) throw error
  }

  const propose = async (
    requests: EditRequestDTO[],
    send: SendEdit | null = null,
  ) => {
    if (requests.length === 0) return
    setPending({ requests, previews: null, problem: null, send })
    try {
      stillAsked(requests, {
        previews: await Promise.all(requests.map(previewEdit)),
      })
    } catch (error) {
      stillAsked(requests, { problem: messageOf(error) })
    }
  }

  // A confirm that saved or sent part of the edits closes: that part would be refused a second
  // time, and a save's toast already offers its undo. A failure on the first stays in the dialog.
  // True when anything went through.
  const confirm = async () => {
    if (!pending?.previews) return false
    const { requests, previews, send } = pending
    setSaving(true)
    const { done, error } = send
      ? await runInOrder(requests, send)
      : await saveEach(requests)
    setSaving(false)
    if (!error) {
      setPending(null)
      if (send)
        toast({ title: 'Change sent', description: summaryOf(previews) })
    } else if (done.length === 0) {
      stillAsked(requests, { problem: messageOf(error) })
    } else {
      setPending(null)
      errorToast(
        `${send ? 'Sent' : 'Saved'} ${done.length} of ${requests.length} changes`,
        messageOf(error),
      )
    }
    return done.length > 0
  }

  return {
    pending,
    saving,
    propose,
    confirm,
    cancel: () => setPending(null),
    commit,
    undo,
  }
}

export type ConfirmedEdits = ReturnType<typeof useConfirmedEdits>
